/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IMediaGalleryThumbnailItemViewProps, IMediaGalleryThumbnailsViewProps, IMediaGalleryViewProps } from '@msdyn365-commerce-modules/media-gallery';

//----------------------------------------------------------
// @CUSTOM: Added imports for customizations
//----------------------------------------------------------
import { IMediaGalleryData } from '@msdyn365-commerce-modules/media-gallery';
import { IMediaGalleryProps } from '../definition-extensions/media-gallery.ext.props.autogenerated';
import LookInside from './custom-components/look-inside.component';
//----------------------------------------------------------

const MediaGalleryView: React.FC<IMediaGalleryViewProps & IMediaGalleryProps<{}>> = props => {     // @CUSTOM: Added extended definition props
    const { CarouselProps, Thumbnails, MediaGallery, Modal, config, context, resources } = props;    // @CUSTOM: Added config, context, resources from extended definition props

    //------------------------------------------------------
    // @CUSTOM: Variables to pass to LookInside component
    //------------------------------------------------------
    const product = (MediaGallery.moduleProps.data as IMediaGalleryData).product.result;
    const formattedPrice = product && context.cultureFormatter.formatCurrency(product.Price);
    //------------------------------------------------------

    return (
        <Module {...MediaGallery}>
            <Node {...CarouselProps} />
            {Modal}
            {_renderThumbnails(Thumbnails)}
            {/* @CUSTOM: Render LookInside custom component below if enabled in config */}
            {config.enableLookInside && product && product.ItemId &&
                <LookInside
                    resources={resources}
                    imageUrl={context.request.apiSettings.baseImageUrl}
                    productId={product.ItemId}
                    productName={product.Name}
                    productPrice={formattedPrice}
                    maxCount={config.lookInsideMaxPages}
                />
            }
        </Module>
    );
};

// @CUSTOM: Adjusted result type to include null when only one product image or less
const _renderThumbnails = (thumbnails: IMediaGalleryThumbnailsViewProps): JSX.Element | null => {
    const { ThumbnailsContainerProps, SingleSlideCarouselComponentProps, items } = thumbnails;

    //------------------------------------------------------
    // @CUSTOM: If there's only one product image or less,
    // do not render thumbnails
    //------------------------------------------------------
    if (items && items.length <= 1) {
        return null;
    }
    //------------------------------------------------------

    return (
        <Node {...ThumbnailsContainerProps}>
            <Node {...SingleSlideCarouselComponentProps}>{items && items.map(_renderThumbnailItem)}</Node>
        </Node>
    );
};

const _renderThumbnailItem = (thumbnail: IMediaGalleryThumbnailItemViewProps): JSX.Element => {
    const { ThumbnailItemContainerProps, Picture } = thumbnail;

    return <Node {...ThumbnailItemContainerProps}>{Picture}</Node>;
};

export default MediaGalleryView;
